import { useMemo, useState } from "react"
import { useParams, useNavigate } from "@tanstack/react-router"
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Play,
  RefreshCw,
  Activity,
  AlertTriangle,
  Calendar,
  Timer,
  Layers,
  TrendingUp,
  Globe,
  Code,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Moon,
  Bell,
  Key,
  ExternalLink,
} from "lucide-react"
import toast from "react-hot-toast"

import { Button } from "@aix/ui"
import { trpc } from "~/lib/trpc"

interface ScenarioDefinition {
  id: string
  name: string
  project: string
  critical: boolean
  schedule: string
  runbook?: string
  dashboardUrl?: string
  steps: {
    name: string
    request: {
      method: "GET" | "POST"
      endpoint: string
      input?: Record<string, unknown>
      auth?: "lomin7" | "none"
    }
    assert: {
      status?: number
      latencyMs?: number
      bodyPath?: Record<string, unknown>
      bodyContains?: string[]
    }
    extract?: Record<string, string>
  }[]
}

/**
 * Scenario Detail Screen
 * Shows in-depth details for a single monitoring scenario@env combo
 */
function ScenarioDetailScreen() {
  const navigate = useNavigate()
  // Route param is now the UUID (id) which identifies both scenario + environment
  const { scenarioId: id } = useParams({ strict: false }) as { scenarioId: string }

  // Fetch scenario data from DB
  const { data: dbScenarios, isLoading: dbLoading, refetch } = trpc.apiMonitors.listScenarios.useQuery()

  // Fetch run history for this scenario@env combo (uses UUID)
  const {
    data: runHistory,
    isLoading: historyLoading,
    refetch: refetchHistory,
  } = trpc.apiMonitors.getRunHistory.useQuery({ id, limit: 50 }, { enabled: !!id })

  // Fetch project configs (contains environment URLs synced from source repos)
  const { data: projectConfigs } = trpc.apiMonitors.listProjects.useQuery()

  // Trigger manual run mutation - environment is now implicit from the scenario row
  const triggerMutation = trpc.apiMonitors.triggerRun.useMutation({
    onSuccess: () => {
      // Refetch both scenario list and run history for real-time update
      void refetch()
      void refetchHistory()
    },
  })

  // Snooze/unsnooze mutations
  const snoozeMutation = trpc.apiMonitors.snoozeScenario.useMutation({
    onSuccess: (_data, variables) => {
      const v = variables as { duration?: string; id?: string } | undefined
      toast.success(`Snoozed for ${v?.duration ?? ""}`)
      void refetch()
    },
    onError: (e) => toast.error(e.message || "Failed to snooze"),
  })

  const unsnoozeMutation = trpc.apiMonitors.unsnoozeScenario.useMutation({
    onSuccess: () => {
      toast.success("Unsnoozed")
      void refetch()
    },
    onError: (e) => toast.error(e.message || "Failed to unsnooze"),
  })

  // Find the scenario by UUID (id)
  const scenario = useMemo(() => {
    const dbScenario = dbScenarios?.find((s) => s.id === id)

    if (!dbScenario) return null

    const project = dbScenario.project || "unknown"
    const definition = getScenarioDefinition(dbScenario.definition)
    const lastRun = dbScenario.lastRun

    return {
      id: dbScenario.id,
      scenarioId: dbScenario.scenarioId,
      name: dbScenario.name || dbScenario.scenarioId,
      project,
      environment: dbScenario.environment,
      schedule: dbScenario.schedule || "1h",
      status: (dbScenario.status as "healthy" | "failing" | "unknown" | "degraded") || "unknown",
      lastRun: lastRun
        ? {
            createdAt: typeof lastRun.createdAt === "string" ? lastRun.createdAt : lastRun.createdAt.toISOString(),
            duration: lastRun.duration,
            success: lastRun.success,
            error: lastRun.error || undefined,
            failedStep: lastRun.failedStep || undefined,
          }
        : undefined,
      stepCount: dbScenario.stepCount || 0,
      stepNames: dbScenario.stepNames || [],
      critical: dbScenario.critical || false,
      runbook: dbScenario.runbook ?? definition?.runbook,
      dashboardUrl: dbScenario.dashboardUrl ?? definition?.dashboardUrl,
      // Get full definition from DB (includes step details)
      definition,
      // Get base URLs from project config (synced from source repos)
      baseUrls: getProjectUrls(project, projectConfigs),
      isSnoozed: dbScenario.isSnoozed ?? false,
      snoozedUntil: dbScenario.snoozedUntil,
    }
  }, [dbScenarios, id, projectConfigs])

  // Fetch secrets for this project
  const { data: projectSecrets } = trpc.secrets.list.useQuery(
    { project: scenario?.project },
    { enabled: !!scenario?.project },
  )

  // Calculate stats from run history
  const stats = useMemo(() => {
    if (!runHistory?.runs) return null

    const runs = runHistory.runs
    const total = runs.length
    const successful = runs.filter((r) => r.success).length
    const failed = total - successful
    const avgDuration = total > 0 ? Math.round(runs.reduce((acc, r) => acc + r.duration, 0) / total) : 0

    // Last 24h runs
    const now = new Date()
    const last24h = runs.filter((r) => {
      const runDate = new Date(r.createdAt)
      return now.getTime() - runDate.getTime() < 24 * 60 * 60 * 1000
    })
    const last24hSuccess = last24h.filter((r) => r.success).length
    const last24hRate = last24h.length > 0 ? Math.round((last24hSuccess / last24h.length) * 100) : 0

    // Last 7d runs
    const last7d = runs.filter((r) => {
      const runDate = new Date(r.createdAt)
      return now.getTime() - runDate.getTime() < 7 * 24 * 60 * 60 * 1000
    })
    const last7dSuccess = last7d.filter((r) => r.success).length
    const last7dRate = last7d.length > 0 ? Math.round((last7dSuccess / last7d.length) * 100) : 0

    return {
      total,
      successful,
      failed,
      avgDuration,
      successRate: total > 0 ? Math.round((successful / total) * 100) : 0,
      last24hRate,
      last7dRate,
    }
  }, [runHistory])

  const [copiedAll, setCopiedAll] = useState(false)
  const [copiedSteps, setCopiedSteps] = useState(false)
  const [copiedRunbook, setCopiedRunbook] = useState(false)

  // Format scenario for LLM debugging
  const formatScenarioForLLM = () => {
    if (!scenario) return ""

    const lines: string[] = []
    lines.push(`# API Monitor Scenario: ${scenario.name}`)
    lines.push("")
    lines.push("## Overview")
    lines.push(`- **Scenario ID:** ${scenario.scenarioId}`)
    lines.push(`- **Project:** ${scenario.project}`)
    lines.push(`- **Status:** ${scenario.status}`)
    lines.push(`- **Schedule:** ${formatSchedule(scenario.schedule)}`)
    lines.push(`- **Critical:** ${scenario.critical ? "Yes" : "No"}`)
    if (scenario.dashboardUrl) {
      lines.push(`- **Dashboard:** ${scenario.dashboardUrl}`)
    }
    if (scenario.runbook) {
      lines.push(`- **Agent Runbook:** \`${scenario.runbook}\``)
    }
    lines.push("")

    // Base URLs
    lines.push("## Target URLs")
    Object.entries(scenario.baseUrls).forEach(([env, url]) => {
      lines.push(`- **${env}:** ${url}`)
    })
    lines.push("")

    // Last Run
    if (scenario.lastRun) {
      lines.push("## Last Run")
      lines.push(`- **Status:** ${scenario.lastRun.success ? "SUCCESS" : "FAILED"}`)
      lines.push(`- **Duration:** ${scenario.lastRun.duration}ms`)
      lines.push(`- **Time:** ${formatDate(new Date(scenario.lastRun.createdAt))}`)
      if (scenario.lastRun.failedStep) {
        lines.push(`- **Failed Step:** ${scenario.lastRun.failedStep}`)
      }
      if (scenario.lastRun.error) {
        lines.push(`- **Error:** ${scenario.lastRun.error}`)
      }
      lines.push("")
    }

    // Steps
    if (scenario.definition?.steps) {
      lines.push("## Scenario Steps")
      lines.push("")
      scenario.definition.steps.forEach((step, i) => {
        const baseUrl = scenario.baseUrls.prod || scenario.baseUrls.dev || ""
        lines.push(`### Step ${i + 1}: ${step.name}`)
        lines.push(`- **Method:** ${step.request.method}`)
        lines.push(`- **Endpoint:** ${step.request.endpoint}`)
        lines.push(`- **Full URL:** ${baseUrl}/api/trpc/${step.request.endpoint}`)
        if (step.request.auth) {
          lines.push(`- **Auth:** ${step.request.auth}`)
        }
        if (step.request.input && Object.keys(step.request.input).length > 0) {
          lines.push(`- **Input:**`)
          lines.push("```json")
          lines.push(JSON.stringify(step.request.input, null, 2))
          lines.push("```")
        }
        lines.push(`- **Assertions:**`)
        if (step.assert.status) lines.push(`  - Status: ${step.assert.status}`)
        if (step.assert.latencyMs) lines.push(`  - Max Latency: ${step.assert.latencyMs}ms`)
        if (step.assert.bodyPath && Object.keys(step.assert.bodyPath).length > 0) {
          lines.push(`  - Body Path Checks: ${JSON.stringify(step.assert.bodyPath)}`)
        }
        if (step.assert.bodyContains && step.assert.bodyContains.length > 0) {
          lines.push(`  - Body Contains: ${step.assert.bodyContains.join(", ")}`)
        }
        if (step.extract && Object.keys(step.extract).length > 0) {
          lines.push(`- **Extracts:**`)
          Object.entries(step.extract).forEach(([varName, path]) => {
            lines.push(`  - {{${varName}}} ← ${path}`)
          })
        }
        lines.push("")
      })
    } else if (scenario.stepNames.length > 0) {
      lines.push("## Steps")
      scenario.stepNames.forEach((step, i) => {
        lines.push(`${i + 1}. ${step}`)
      })
      lines.push("")
    }

    // Stats
    if (stats) {
      lines.push("## Statistics")
      lines.push(`- **24h Success Rate:** ${stats.last24hRate}%`)
      lines.push(`- **7d Success Rate:** ${stats.last7dRate}%`)
      lines.push(`- **Average Duration:** ${stats.avgDuration}ms`)
      lines.push(`- **Total Runs:** ${stats.total}`)
      lines.push(`- **Successful:** ${stats.successful}`)
      lines.push(`- **Failed:** ${stats.failed}`)
    }

    return lines.join("\n")
  }

  // Format just the steps for copying
  const formatStepsForLLM = () => {
    if (!scenario?.definition?.steps) return ""

    const lines: string[] = []
    lines.push(`# Scenario Steps: ${scenario.name}`)
    lines.push("")

    scenario.definition.steps.forEach((step, i) => {
      const baseUrl = scenario.baseUrls.prod || scenario.baseUrls.dev || ""
      lines.push(`## Step ${i + 1}: ${step.name}`)
      lines.push(`- **Method:** ${step.request.method}`)
      lines.push(`- **Endpoint:** ${step.request.endpoint}`)
      lines.push(`- **Full URL:** ${baseUrl}/api/trpc/${step.request.endpoint}`)
      if (step.request.auth) {
        lines.push(`- **Auth:** ${step.request.auth}`)
      }
      if (step.request.input && Object.keys(step.request.input).length > 0) {
        lines.push(`- **Input:**`)
        lines.push("```json")
        lines.push(JSON.stringify(step.request.input, null, 2))
        lines.push("```")
      }
      lines.push(`- **Assertions:**`)
      if (step.assert.status) lines.push(`  - Status: ${step.assert.status}`)
      if (step.assert.latencyMs) lines.push(`  - Max Latency: ${step.assert.latencyMs}ms`)
      if (step.assert.bodyPath && Object.keys(step.assert.bodyPath).length > 0) {
        lines.push(`  - Body Path Checks: ${JSON.stringify(step.assert.bodyPath)}`)
      }
      if (step.assert.bodyContains && step.assert.bodyContains.length > 0) {
        lines.push(`  - Body Contains: ${step.assert.bodyContains.join(", ")}`)
      }
      if (step.extract && Object.keys(step.extract).length > 0) {
        lines.push(`- **Extracts:**`)
        Object.entries(step.extract).forEach(([varName, path]) => {
          lines.push(`  - {{${varName}}} ← ${path}`)
        })
      }
      lines.push("")
    })

    return lines.join("\n")
  }

  const handleCopyAll = async () => {
    const text = formatScenarioForLLM()
    await navigator.clipboard.writeText(text)
    setCopiedAll(true)
    toast.success("Copied scenario to clipboard")
    setTimeout(() => setCopiedAll(false), 2000)
  }

  const handleCopySteps = async () => {
    const text = formatStepsForLLM()
    await navigator.clipboard.writeText(text)
    setCopiedSteps(true)
    toast.success("Copied steps to clipboard")
    setTimeout(() => setCopiedSteps(false), 2000)
  }

  const handleCopyRunbook = async () => {
    if (!scenario?.runbook) return

    await navigator.clipboard.writeText(scenario.runbook)
    setCopiedRunbook(true)
    toast.success("Copied runbook command")
    setTimeout(() => setCopiedRunbook(false), 2000)
  }

  const isLoading = dbLoading

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2" />
          <div className="text-muted-foreground">Loading scenario...</div>
        </div>
      </div>
    )
  }

  if (!scenario) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-background">
        <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
        <div className="text-lg font-medium text-foreground mb-2">Scenario Not Found</div>
        <div className="text-muted-foreground mb-4">The scenario could not be found.</div>
        <Button variant="outline" onClick={() => navigate({ to: "/devtools/monitors" })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Monitors
        </Button>
      </div>
    )
  }

  // Environment is now implicit - no selector needed
  const handleTriggerRun = async () => {
    await triggerMutation.mutateAsync({ id: scenario.id })
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border p-4">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/devtools/monitors" })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <StatusIcon status={scenario.status} size="lg" />
              <h1 className="text-xl font-semibold text-foreground">{scenario.name}</h1>
              <EnvironmentBadge environment={scenario.environment} />
              <Badge className={scenario.critical ? "bg-red-500/20 text-red-400" : "bg-border text-muted-foreground"}>
                {scenario.critical ? "Critical" : "Standard"}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              <span className="font-mono">{scenario.scenarioId}</span>
              <span>•</span>
              <span>{scenario.project}</span>
              <span>•</span>
              <span>Every {formatSchedule(scenario.schedule)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleCopyAll}>
              {copiedAll ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              <span className="ml-1.5">Copy All</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void refetch()
                void refetchHistory()
              }}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {/* Run button - environment is implicit from scenario */}
            <Button size="sm" onClick={handleTriggerRun} disabled={triggerMutation.isLoading}>
              <Play className="h-4 w-4 mr-2" />
              {triggerMutation.isLoading ? "Running..." : "Run"}
            </Button>

            {/* Snooze controls */}
            {scenario.isSnoozed ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => unsnoozeMutation.mutate({ id: scenario.id })}
                disabled={unsnoozeMutation.isLoading}
                className="text-yellow-400 border-yellow-500/40"
              >
                <Bell className="h-4 w-4 mr-1" /> Unsnooze
              </Button>
            ) : (
              <div className="flex items-center gap-1">
                {(["1h", "24h", "7d"] as const).map((d) => (
                  <Button
                    key={d}
                    variant="outline"
                    size="sm"
                    onClick={() => snoozeMutation.mutate({ id: scenario.id, duration: d })}
                    disabled={snoozeMutation.isLoading}
                  >
                    <Moon className="h-3 w-3 mr-1" /> {d}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Snoozed banner */}
        {scenario.isSnoozed && scenario.snoozedUntil && (
          <div className="mx-4 mt-2 px-3 py-2 rounded bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm flex items-center gap-2">
            <Moon className="h-4 w-4" />
            Alerts snoozed until {new Date(scenario.snoozedUntil).toLocaleString()}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={<Activity className="h-5 w-5" />}
              label="Status"
              value={scenario.status.toUpperCase()}
              valueColor={
                scenario.status === "healthy"
                  ? "text-green-500"
                  : scenario.status === "failing"
                    ? "text-red-500"
                    : "text-yellow-500"
              }
            />
            <StatCard
              icon={<TrendingUp className="h-5 w-5" />}
              label="24h Success Rate"
              value={stats ? `${stats.last24hRate}%` : "-"}
              valueColor={
                stats && stats.last24hRate >= 90
                  ? "text-green-500"
                  : stats && stats.last24hRate >= 70
                    ? "text-yellow-500"
                    : "text-red-500"
              }
            />
            <StatCard
              icon={<Calendar className="h-5 w-5" />}
              label="7d Success Rate"
              value={stats ? `${stats.last7dRate}%` : "-"}
              valueColor={
                stats && stats.last7dRate >= 90
                  ? "text-green-500"
                  : stats && stats.last7dRate >= 70
                    ? "text-yellow-500"
                    : "text-red-500"
              }
            />
            <StatCard
              icon={<Timer className="h-5 w-5" />}
              label="Avg Duration"
              value={stats ? `${stats.avgDuration}ms` : "-"}
            />
          </div>

          {/* Scenario Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Configuration & URLs */}
            <div className="space-y-6">
              <div className="bg-secondary rounded-lg p-4">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  Configuration
                </h3>
                <div className="space-y-3 text-sm">
                  <DetailRow label="Scenario ID" value={scenario.scenarioId} mono />
                  <DetailRow label="Project" value={scenario.project} />
                  <DetailRow label="Schedule" value={formatSchedule(scenario.schedule)} />
                  <DetailRow label="Step Count" value={String(scenario.stepCount)} />
                  <DetailRow
                    label="Critical"
                    value={scenario.critical ? "Yes" : "No"}
                    valueColor={scenario.critical ? "text-red-400" : undefined}
                  />
                </div>
              </div>

              {/* Base URLs */}
              <div className="bg-secondary rounded-lg p-4">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Target URLs
                </h3>
                <div className="space-y-2 text-sm">
                  {Object.entries(scenario.baseUrls).map(([env, url]) => (
                    <div key={env} className="flex justify-between items-center">
                      <span className="text-muted-foreground capitalize">{env}:</span>
                      <code className="text-xs bg-background px-2 py-1 rounded font-mono text-foreground">{url}</code>
                    </div>
                  ))}
                </div>
              </div>

              {(scenario.dashboardUrl || scenario.runbook) && (
                <div className="bg-secondary rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Code className="h-4 w-4" />
                      Agent Debug
                    </h3>
                    {scenario.dashboardUrl && (
                      <a
                        href={scenario.dashboardUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        Axiom <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <div className="space-y-3 text-sm">
                    {scenario.dashboardUrl && (
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">Dashboard</span>
                        <a
                          href={scenario.dashboardUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-primary hover:underline break-all"
                        >
                          {scenario.dashboardUrl}
                        </a>
                      </div>
                    )}
                    {scenario.runbook && (
                      <div className="flex flex-col gap-2">
                        <span className="text-xs text-muted-foreground">Runbook Command</span>
                        <div className="flex items-start gap-2">
                          <code className="flex-1 text-xs bg-background px-2 py-1.5 rounded font-mono text-foreground break-all">
                            {scenario.runbook}
                          </code>
                          <Button variant="outline" size="sm" onClick={handleCopyRunbook} className="h-8 px-2">
                            {copiedRunbook ? (
                              <Check className="h-3.5 w-3.5 text-green-500" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Secrets for this project */}
              {projectSecrets && projectSecrets.length > 0 && (
                <div className="bg-secondary rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      Secrets ({scenario.project})
                    </h3>
                    <button
                      onClick={() => navigate({ to: "/devtools/secrets" })}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      Manage <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="space-y-2 text-sm">
                    {(
                      projectSecrets as { id: string; key: string; environment: string; description: string | null }[]
                    ).map((secret) => (
                      <div key={secret.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <code className="text-xs font-mono text-foreground">{secret.key}</code>
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${
                              secret.environment === "prod"
                                ? "bg-green-500/20 text-green-400"
                                : secret.environment === "dev"
                                  ? "bg-blue-500/20 text-blue-400"
                                  : "bg-gray-500/20 text-gray-400"
                            }`}
                          >
                            {secret.environment}
                          </span>
                        </div>
                        {secret.description && (
                          <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {secret.description}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Last Run */}
            <div className="bg-secondary rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Last Run
              </h3>
              {scenario.lastRun ? (
                <div className="space-y-3 text-sm">
                  <DetailRow
                    label="Status"
                    value={scenario.lastRun.success ? "SUCCESS" : "FAILED"}
                    valueColor={scenario.lastRun.success ? "text-green-400" : "text-red-400"}
                  />
                  <DetailRow label="Duration" value={`${scenario.lastRun.duration}ms`} />
                  <DetailRow label="Time" value={formatDate(new Date(scenario.lastRun.createdAt))} />
                  {scenario.lastRun.failedStep && (
                    <DetailRow label="Failed Step" value={scenario.lastRun.failedStep} valueColor="text-red-400" />
                  )}
                  {scenario.lastRun.error && (
                    <div className="mt-3">
                      <span className="text-muted-foreground text-xs">Error:</span>
                      <div className="mt-1 p-2 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs font-mono break-all">
                        {scenario.lastRun.error}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">No runs recorded yet</div>
              )}
            </div>
          </div>

          {/* Steps Definition */}
          {scenario.definition && (
            <div className="bg-secondary rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Code className="h-4 w-4" />
                  Scenario Steps
                </h3>
                <Button variant="ghost" size="sm" onClick={handleCopySteps} className="h-7 px-2">
                  {copiedSteps ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                  <span className="ml-1.5 text-xs">Copy Steps</span>
                </Button>
              </div>
              <div className="space-y-4">
                {scenario.definition.steps.map((step, i) => (
                  <StepCard
                    key={i}
                    step={step}
                    index={i}
                    baseUrl={scenario.baseUrls.prod || scenario.baseUrls.dev || ""}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Fallback if no definition - show step names */}
          {!scenario.definition && scenario.stepNames.length > 0 && (
            <div className="bg-secondary rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Code className="h-4 w-4" />
                Steps
              </h3>
              <ol className="space-y-1.5">
                {scenario.stepNames.map((step, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-muted-foreground font-mono text-xs mt-0.5">{i + 1}.</span>
                    <span className="text-foreground">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Run History */}
          <div className="bg-secondary rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Run History
              {runHistory && <span className="text-muted-foreground font-normal">({runHistory.total} total)</span>}
            </h3>

            {historyLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2" />
                Loading history...
              </div>
            ) : runHistory?.runs && runHistory.runs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs text-muted-foreground">
                      <th className="pb-2 pr-4">Status</th>
                      <th className="pb-2 pr-4">Time</th>
                      <th className="pb-2 pr-4">Duration</th>
                      <th className="pb-2 pr-4">Trigger</th>
                      <th className="pb-2">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runHistory.runs.map((run) => (
                      <tr key={run.id} className="border-b border-border/50">
                        <td className="py-2 pr-4">
                          <span
                            className={`flex items-center gap-1.5 ${run.success ? "text-green-400" : "text-red-400"}`}
                          >
                            {run.success ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                            {run.success ? "Success" : "Failed"}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-foreground">{formatDate(new Date(run.createdAt))}</td>
                        <td className="py-2 pr-4 text-foreground font-mono">{run.duration}ms</td>
                        <td className="py-2 pr-4">
                          <EnvironmentBadge environment={run.environment || "prod"} />
                        </td>
                        <td className="py-2 text-red-400 text-xs truncate max-w-xs">{run.error || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground text-center py-8">No run history available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Step Card Component - displays a single step with its endpoint and assertions
function StepCard({ step, index, baseUrl }: { step: ScenarioDefinition["steps"][0]; index: number; baseUrl: string }) {
  const [expanded, setExpanded] = useState(false)
  const fullUrl = `${baseUrl}/api/trpc/${step.request.endpoint}`

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Step Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-background/50 transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        <span className="text-xs font-mono text-muted-foreground w-6">{index + 1}.</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-foreground">{step.name}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
            <span
              className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                step.request.method === "GET" ? "bg-green-500/20 text-green-400" : "bg-blue-500/20 text-blue-400"
              }`}
            >
              {step.request.method}
            </span>
            <code className="truncate">{step.request.endpoint}</code>
          </div>
        </div>
        {step.request.auth && (
          <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">{step.request.auth}</span>
        )}
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-border bg-background/30 space-y-4">
          {/* Full URL */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">FULL URL</div>
            <code className="text-xs bg-background px-2 py-1.5 rounded block font-mono text-foreground break-all">
              {fullUrl}
            </code>
          </div>

          {/* Input */}
          {step.request.input && Object.keys(step.request.input).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">INPUT</div>
              <pre className="text-xs bg-background px-2 py-1.5 rounded font-mono text-foreground overflow-x-auto">
                {JSON.stringify(step.request.input, null, 2)}
              </pre>
            </div>
          )}

          {/* Assertions */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">ASSERTIONS</div>
            <div className="space-y-1 text-xs">
              {step.assert.status && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Status:</span>
                  <code className="bg-background px-1.5 py-0.5 rounded text-foreground">{step.assert.status}</code>
                </div>
              )}
              {step.assert.latencyMs && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Max Latency:</span>
                  <code className="bg-background px-1.5 py-0.5 rounded text-foreground">{step.assert.latencyMs}ms</code>
                </div>
              )}
              {step.assert.bodyPath && Object.keys(step.assert.bodyPath).length > 0 && (
                <div>
                  <span className="text-muted-foreground">Body Path Checks:</span>
                  <pre className="mt-1 bg-background px-2 py-1.5 rounded font-mono text-foreground overflow-x-auto">
                    {JSON.stringify(step.assert.bodyPath, null, 2)}
                  </pre>
                </div>
              )}
              {step.assert.bodyContains && step.assert.bodyContains.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Body Contains:</span>
                  <code className="bg-background px-1.5 py-0.5 rounded text-foreground">
                    {step.assert.bodyContains.join(", ")}
                  </code>
                </div>
              )}
            </div>
          </div>

          {/* Extract */}
          {step.extract && Object.keys(step.extract).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">EXTRACT VARIABLES</div>
              <div className="space-y-1 text-xs">
                {Object.entries(step.extract).map(([varName, path]) => (
                  <div key={varName} className="flex items-center gap-2">
                    <code className="bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">{`{{${varName}}}`}</code>
                    <span className="text-muted-foreground">←</span>
                    <code className="bg-background px-1.5 py-0.5 rounded text-foreground">{path}</code>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Status Icon Component
function StatusIcon({
  status,
  size = "md",
}: {
  status: "healthy" | "failing" | "unknown" | "degraded"
  size?: "md" | "lg"
}) {
  const sizeClass = size === "lg" ? "h-6 w-6" : "h-5 w-5"

  switch (status) {
    case "healthy":
      return <CheckCircle2 className={`${sizeClass} text-green-500 flex-shrink-0`} />
    case "failing":
      return <XCircle className={`${sizeClass} text-red-500 flex-shrink-0`} />
    case "degraded":
      return <Clock className={`${sizeClass} text-yellow-500 flex-shrink-0`} />
    default:
      return <Clock className={`${sizeClass} text-muted-foreground flex-shrink-0`} />
  }
}

// Badge Component
function Badge({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${className}`}>{children}</span>
}

// Environment Badge Component - color-coded by environment
function EnvironmentBadge({ environment }: { environment: string }) {
  const getEnvStyle = () => {
    switch (environment) {
      case "prod":
        return "bg-red-500/20 text-red-400 border-red-500/30"
      case "dev":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30"
      case "local":
        return "bg-gray-500/20 text-gray-400 border-gray-500/30"
      case "manual":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30"
      default:
        return "bg-border text-muted-foreground"
    }
  }

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase border ${getEnvStyle()}`}>{environment}</span>
  )
}

// Stat Card Component
function StatCard({
  icon,
  label,
  value,
  valueColor,
}: {
  icon: React.ReactNode
  label: string
  value: string
  valueColor?: string
}) {
  return (
    <div className="bg-secondary rounded-lg p-4">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${valueColor || "text-foreground"}`}>{value}</div>
    </div>
  )
}

// Detail Row Component
function DetailRow({
  label,
  value,
  mono,
  valueColor,
}: {
  label: string
  value: string
  mono?: boolean
  valueColor?: string
}) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}:</span>
      <span className={`${mono ? "font-mono" : ""} ${valueColor || "text-foreground"}`}>{value}</span>
    </div>
  )
}

// Helper functions
function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

// Schedule can be simple interval or with start time
type Schedule = string | { interval: string; startTime: string }

function formatSchedule(schedule: Schedule): string {
  // Handle object format with start time
  if (typeof schedule === "object") {
    const intervalStr = formatInterval(schedule.interval)
    return `${intervalStr} @ ${schedule.startTime} UTC`
  }
  return formatInterval(schedule)
}

function formatInterval(interval: string): string {
  switch (interval) {
    case "1m":
      return "1 min"
    case "5m":
      return "5 min"
    case "15m":
      return "15 min"
    case "1h":
      return "1 hour"
    case "3h":
      return "3 hours"
    case "6h":
      return "6 hours"
    case "24h":
      return "daily"
    default:
      return interval
  }
}

function getScenarioDefinition(value: unknown): ScenarioDefinition | undefined {
  if (!value || typeof value !== "object") {
    return undefined
  }

  const candidate = value as Partial<ScenarioDefinition>
  if (!candidate.id || !candidate.name || !Array.isArray(candidate.steps)) {
    return undefined
  }

  return candidate as ScenarioDefinition
}

// Helper to extract URLs from project config
function getProjectUrls(
  project: string,
  projectConfigs?: {
    project: string
    environments?: {
      local?: { url: string; enabled: boolean }
      dev?: { url: string; enabled: boolean }
      prod?: { url: string; enabled: boolean }
    } | null
  }[],
): Record<string, string> {
  const config = projectConfigs?.find((c) => c.project === project)
  if (!config?.environments) return {}

  const urls: Record<string, string> = {}
  if (config.environments.local?.url) urls.local = config.environments.local.url
  if (config.environments.dev?.url) urls.dev = config.environments.dev.url
  if (config.environments.prod?.url) urls.prod = config.environments.prod.url

  return urls
}

export default ScenarioDetailScreen
